'use client';

import { useEffect, useRef, useState } from 'react';
import ParticleCanvas from '../home/components/ParticleCanvas';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ImprintPage() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => {
      if (sectionRef?.current) {
        observer?.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <>
      <ParticleCanvas />
      <div className="vignette" aria-hidden="true" />
      <Header />
      <main className="relative min-h-screen pt-32 pb-20 px-6" style={{ zIndex: 2 }}>
        <div 
          ref={sectionRef}
          className={`max-w-[800px] mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Hero Title */}
          <h1 
            className="text-5xl md:text-6xl font-black text-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #c4beff 50%, #6a5cff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Imprint (Legal Notice)
          </h1>

          {/* Subtitle */}
          <p className="text-center text-vanta-muted text-lg mb-16">
            Legal disclosure and operator information.
          </p>

          {/* Content Card */}
          <div 
            className="rounded-3xl p-8 md:p-12"
            style={{
              background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.08) 0%, rgba(139, 127, 255, 0.05) 100%)',
              border: '2px solid rgba(106, 92, 255, 0.2)',
              backdropFilter: 'blur(30px)',
              boxShadow: '0 25px 80px rgba(106, 92, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="space-y-8 text-vanta-text">
              {/* Last Updated */}
              <p className="text-sm text-vanta-muted">
                Last updated: February 22, 2026
              </p>

              {/* Service Provider */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Service Provider</h2>
                <div className="space-y-2 leading-relaxed">
                  <p className="text-xl font-semibold text-white">VantaRust</p>
                  <p className="font-semibold">Owner and Responsible Party:</p>
                  <p>Kevin Erk</p>
                  <p className="font-semibold mt-3">Address:</p>
                  <p>My Post 24 – Bahnhof St. Gallen</p>
                  <p>Lagerstrasse / Bahnhofstrasse Unterführung</p>
                  <p>9000 St. Gallen</p>
                  <p>Switzerland</p>
                  <p className="mt-3">
                    Email: <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors">support@vantarust.com</a>
                  </p>
                  <p>
                    Website: <a href="https://vantarust.com" target="_blank" rel="noopener noreferrer" className="text-vanta-accent hover:text-vanta-accent-light transition-colors">https://vantarust.com</a>
                  </p>
                </div>
              </section>

              {/* Authorized Representative */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Authorized Representative</h2>
                <p className="leading-relaxed">
                  The person named above is solely responsible for the content and operation of the VantaRust website and related services in accordance with applicable law.
                </p>
              </section>

              {/* Description of Services */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Description of Services</h2>
                <div className="space-y-3 leading-relaxed">
                  <p>
                    VantaRust operates privately managed game servers for the video game Rust, as well as related website and community services.
                  </p>
                  <p>
                    VantaRust is an independent service provider and is not affiliated with, endorsed by, or sponsored by Facepunch Studios Ltd, Valve Corporation, Steam, or any of their affiliates.
                  </p>
                  <p>
                    All trademarks, game content, and related materials remain the property of their respective owners.
                  </p>
                </div>
              </section>

              {/* Liability for Content */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Liability for Content</h2>
                <div className="space-y-3 leading-relaxed">
                  <p>
                    The content of this website has been created with the greatest possible care. However, VantaRust makes no representations or warranties regarding the accuracy, completeness, or timeliness of the information provided.
                  </p>
                  <p>
                    As a service provider, VantaRust is responsible for its own content in accordance with applicable law. However, VantaRust is not obligated to monitor transmitted or stored third-party information or investigate circumstances that indicate illegal activity.
                  </p>
                </div>
              </section>

              {/* Liability for External Links */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Liability for External Links</h2>
                <div className="space-y-3 leading-relaxed">
                  <p>
                    This website may contain links to external third-party websites. VantaRust has no control over the content of those websites and assumes no liability for such external content.
                  </p>
                  <p>
                    The respective operator of the linked websites is solely responsible for their content.
                  </p>
                </div>
              </section>

              {/* Intellectual Property and Copyright */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property and Copyright</h2>
                <div className="space-y-3 leading-relaxed">
                  <p>
                    All content created and published by VantaRust, including but not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Website design</li>
                    <li>Text content</li>
                    <li>Graphics</li>
                    <li>Logos</li>
                    <li>Server configurations</li>
                  </ul>
                  <p>
                    is protected under applicable copyright and intellectual property laws.
                  </p>
                  <p>
                    Unauthorized reproduction, modification, distribution, or use is strictly prohibited without prior written permission.
                  </p>
                  <p>
                    Third-party content remains the property of its respective owners.
                  </p>
                </div>
              </section>

              {/* Virtual Services Disclaimer */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Virtual Services Disclaimer</h2>
                <div className="space-y-3 leading-relaxed">
                  <p>
                    VantaRust provides virtual services within the Rust game environment.
                  </p>
                  <p>
                    All virtual items, ranks, features, and data:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Have no real-world ownership value</li>
                    <li>Remain part of the service</li>
                    <li>May be modified, reset, restricted, or removed at any time without notice</li>
                  </ul>
                  <p>
                    VantaRust assumes no liability for loss of virtual data.
                  </p>
                </div>
              </section>

              {/* Legal Contact */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Legal Contact</h2>
                <div className="space-y-3 leading-relaxed">
                  <p>
                    Official legal correspondence must be submitted to:
                  </p>
                  <p>
                    <a href="mailto:support@vantarust.com" className="text-vanta-accent hover:text-vanta-accent-light transition-colors font-semibold">support@vantarust.com</a>
                  </p>
                  <p>
                    VantaRust does not accept legal notices via Discord, in-game chat, or social media platforms.
                  </p>
                </div>
              </section>

              {/* Jurisdiction and Governing Law */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">Jurisdiction and Governing Law</h2>
                <div className="space-y-3 leading-relaxed">
                  <p>
                    These services are governed exclusively by the laws of Switzerland.
                  </p>
                  <p>
                    To the extent permitted by law, the place of jurisdiction shall be St. Gallen, Switzerland.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}